import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { remindForgeAccount } from '@/lib/api/generated/api';
import type { HeldCountdownAction } from '@/shared/components/HeldCountdown';

import type { PersonLook } from '../commitCoverage';
import { COMMIT_SUMMARY_COPY as WORDS } from '../linkedAccountsCopy';
import { organizationKeys } from '../queryKeys';

/**
 * Remind, with Undo (spec 164 US2). A reminder waits as long as the
 * Overview's actions do (spec 154) before it is sent, so a slip costs nothing:
 * Undo before then, and the person is never told. Whatever is still waiting
 * when the page goes away is sent then, not lost.
 */
export const REMIND_HOLD_MS = 6_000;

/** What the service said: sent, or why not. */
export type RemindOutcome = 'sent' | 'too-soon' | 'not-needed' | 'failed';

export interface HeldReminder extends HeldCountdownAction {
  outcome?: RemindOutcome;
}

interface Pending {
  timer: ReturnType<typeof setTimeout>;
  send: (keepalive: boolean) => void;
}

export function useHeldReminders() {
  const queryClient = useQueryClient();
  const [held, setHeld] = useState<Readonly<Record<string, HeldReminder>>>({});
  const [said, setSaid] = useState('');
  const pending = useRef(new Map<string, Pending>());

  const update = useCallback((key: string, change: Partial<HeldReminder>) => {
    setHeld((current) => {
      const entry = current[key];
      return entry ? { ...current, [key]: { ...entry, ...change } } : current;
    });
  }, []);

  const fire = useCallback((key: string, keepalive: boolean) => {
    const entry = pending.current.get(key);
    if (!entry) return;
    clearTimeout(entry.timer);
    pending.current.delete(key);
    entry.send(keepalive);
  }, []);

  const hold = useCallback(
    (look: PersonLook) => {
      if (pending.current.has(look.key)) return;
      const send = (keepalive: boolean) => {
        update(look.key, { state: 'sending' });
        void remindForgeAccount(
          look.userId,
          look.host,
          keepalive ? { keepalive: true } : undefined,
        )
          .then((response): RemindOutcome => {
            if (response.status === 200) return 'sent';
            if (response.status !== 409) return 'failed';
            return response.data.error === 'FORGE_REMINDER_NOT_NEEDED'
              ? 'not-needed'
              : 'too-soon';
          })
          .catch((): RemindOutcome => 'failed')
          .then(async (outcome) => {
            update(look.key, { state: 'sent', outcome });
            if (outcome === 'sent') setSaid(WORDS.reminded(look.person));
            if (outcome !== 'failed') {
              // The list says when each person was reminded, and a link
              // that works now has left the queue.
              await queryClient.invalidateQueries({
                queryKey: organizationKeys.peopleLinks(),
              });
            }
          });
      };
      pending.current.set(look.key, {
        timer: setTimeout(() => {
          fire(look.key, false);
        }, REMIND_HOLD_MS),
        send,
      });
      setHeld((current) => ({
        ...current,
        [look.key]: {
          label: WORDS.reminder,
          sendAt: Date.now() + REMIND_HOLD_MS,
          state: 'held',
        },
      }));
      setSaid(WORDS.heldSaid(look.person, REMIND_HOLD_MS / 1000));
    },
    [fire, queryClient, update],
  );

  const undo = useCallback((key: string) => {
    const entry = pending.current.get(key);
    if (!entry) return;
    clearTimeout(entry.timer);
    pending.current.delete(key);
    setHeld((current) => without(current, key));
    setSaid(WORDS.undone);
  }, []);

  /** Forget a refusal, so the row offers Remind again. */
  const clear = useCallback((key: string) => {
    setHeld((current) => without(current, key));
  }, []);

  useEffect(() => {
    const map = pending.current;
    const sendAll = (keepalive: boolean) => {
      for (const key of [...map.keys()]) fire(key, keepalive);
    };
    const onPageHide = () => {
      sendAll(true);
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      sendAll(false);
    };
  }, [fire]);

  return { held, said, hold, undo, clear };
}

function without<T>(
  record: Readonly<Record<string, T>>,
  key: string,
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(record).filter(([each]) => each !== key),
  );
}
